import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  DiscussionError,
  getDiscussion,
  type DiscussionSort,
  postComment,
  type CommentAnchor,
  type DiscussionKind,
  type DiscussionThread as Thread
} from '@peruselab/api-client/discussions';
import { Icon } from '../../components/Icon';
import { usePublicAccess } from '../public/publicAccess';
import { CommentComposer } from './CommentComposer';
import { CommentItem, defaultAnchorLabel } from './CommentItem';
import { countComments, findComment } from './discussionTree';
import { anchorKey } from './selectionAnchor';
import { DiscussionGuidelines } from './DiscussionGuidelines';

/** ADR 0016 D4: levels shown before "Continue this thread". */
const DEPTH_DESKTOP = 6;
const DEPTH_PHONE = 3;
const PHONE_QUERY = '(max-width: 640px)';

function subscribePhone(onChange: () => void) {
  const query = globalThis.matchMedia?.(PHONE_QUERY);
  query?.addEventListener('change', onChange);
  return () => query?.removeEventListener('change', onChange);
}

export interface DiscussionThreadProps {
  readonly kind: DiscussionKind;
  readonly artifactId: string;
  /** Words for an anchor ("About atom C5"); the host knows its own structure. */
  readonly describeAnchor?: (anchor: CommentAnchor) => string;
  /** Names the thread in its heading ("This drawing"); "Discussion" when absent. */
  readonly label?: string;
  /**
   * Makes the heading a link to the artifact's own page (the workspace's "This molecule", #904).
   * It opens in a new tab, so the workspace -- the paper and its search results -- stays put.
   */
  readonly labelHref?: string;
  /** What a new comment is about, from the host's selection (the workspace); clearable. */
  readonly selectionAnchor?: CommentAnchor | null;
  /** Shows an anchored comment's atoms and bonds (the workspace selects them on the paper). */
  readonly onShowAnchor?: (anchor: CommentAnchor) => void;
  /** The number of comments each time the thread loads, for a count shown elsewhere (a tab badge). */
  readonly onCountChange?: (count: number) => void;
  /** The page hosting this thread shows something private (the workspace's private drawing), #900. */
  readonly hostPrivate?: boolean;
  /**
   * #910: open the composer (with {@link selectionAnchor}) and bring the thread into view -- each new
   * value is a new request (the factsheet's "Discuss" on a statement).
   */
  readonly writeRequest?: number;
}

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly thread: Thread }
  | { readonly status: 'error'; readonly kind: DiscussionError['kind'] };

/**
 * ADR 0016 phase 1: one artifact's discussion, the same component on every artifact page. Who may
 * read and write comes from the artifact's own access rules on the server (principle 2); this only
 * reflects them -- signed out it's read-only with "Sign in to comment" (D3).
 */
export function DiscussionThread(props: DiscussionThreadProps) {
  const {
    kind,
    artifactId,
    describeAnchor,
    label,
    labelHref,
    selectionAnchor = null,
    onShowAnchor,
    onCountChange,
    hostPrivate,
    writeRequest
  } = props;
  const { t } = useTranslation();
  const { signedIn, signIn } = usePublicAccess();
  const location = useLocation();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [focusId, setFocusId] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  // Clearing the "About: …" chip holds for that selection only; selecting something else offers it again.
  const [clearedAnchorKey, setClearedAnchorKey] = useState<string | null>(null);
  const selectionKey = anchorKey(selectionAnchor);
  const anchor = selectionAnchor && clearedAnchorKey !== selectionKey ? selectionAnchor : null;
  useEffect(() => {
    if (!writeRequest) return;
    setClearedAnchorKey(null);
    setWriting(true);
    sectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [writeRequest]);
  const phone = useSyncExternalStore(subscribePhone, () => globalThis.matchMedia?.(PHONE_QUERY).matches ?? false);

  // A ref, so a host passing a fresh callback each render doesn't reload the thread.
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => {
    onCountChangeRef.current = onCountChange;
  });

  // D24 (#910): the conversation's order, or the comments citing the most sources first.
  const [sort, setSort] = useState<DiscussionSort>('oldest');

  const reload = useCallback(async () => {
    try {
      const thread = await getDiscussion(kind, artifactId, sort);
      setState({ status: 'ready', thread });
      onCountChangeRef.current?.(countComments(thread.comments));
    } catch (error) {
      setState({ status: 'error', kind: error instanceof DiscussionError ? error.kind : 'unknown' });
    }
  }, [kind, artifactId, sort]);

  useEffect(() => {
    setState({ status: 'loading' });
    setFocusId(null);
    void reload();
  }, [reload]);

  const headingText =
    state.status === 'ready'
      ? label
        ? t('discussions.headingLabelled', { label, count: countComments(state.thread.comments) })
        : t('discussions.heading', { count: countComments(state.thread.comments) })
      : (label ?? t('discussions.headingPlain'));
  const heading = (
    <h2 id={`discussion-${artifactId}`} className="discussion-heading">
      <Icon name="forum" />
      {labelHref ? (
        <Link to={labelHref} target="_blank" rel="noopener" className="discussion-heading-link">
          {headingText}
          <Icon name="openInNew" size="0.95rem" />
        </Link>
      ) : (
        <span>{headingText}</span>
      )}
    </h2>
  );
  // #900: a Molecule is shared by every drawing of the same structure (#615), so its thread reaches
  // further than any one drawing -- say so, and say it louder next to a private drawing.
  const sharedNote =
    kind === 'molecule' ? (
      <p className="discussion-shared-note" dir="auto">
        <Icon name="public" size="1rem" />
        <span>
          {t('discussions.sharedMolecule')}
          {hostPrivate ? ` ${t('discussions.sharedMoleculePrivate')}` : ''}
        </span>
      </p>
    ) : null;

  if (state.status !== 'ready') {
    return (
      <section ref={sectionRef} className="discussion" aria-labelledby={`discussion-${artifactId}`}>
        {heading}
        {sharedNote}
        {state.status === 'loading' ? <p role="status">{t('common.loading')}</p> : null}
        {state.status === 'error' ? (
          <p className="import-error" role="alert">
            {t(`discussions.errors.${state.kind}`)}
          </p>
        ) : null}
      </section>
    );
  }

  const { thread } = state;
  const focused = focusId ? findComment(thread.comments, focusId) : null;
  const shown = focused ? [focused] : thread.comments;

  return (
    <section ref={sectionRef} className="discussion" aria-labelledby={`discussion-${artifactId}`}>
      {heading}
      {sharedNote}
      <DiscussionGuidelines />
      {thread.canComment ? (
        writing ? (
          <CommentComposer
            anchorLabel={anchor ? (describeAnchor?.(anchor) ?? defaultAnchorLabel(anchor, t)) : null}
            onClearAnchor={() => setClearedAnchorKey(selectionKey)}
            onSubmit={async (body, references) => {
              const referenceIds = references.map((reference) => reference.id);
              await postComment(
                kind,
                artifactId,
                anchor ? { body, anchor, references: referenceIds } : { body, references: referenceIds }
              );
              setWriting(false);
              await reload();
            }}
            onCancel={() => setWriting(false)}
          />
        ) : (
          <button
            type="button"
            className="discussion-start icon-button"
            onClick={() => setWriting(true)}
            aria-label={t('discussions.addComment')}
            title={t('discussions.addComment')}
          >
            <Icon name="addComment" />
          </button>
        )
      ) : !signedIn ? (
        <button
          type="button"
          className="icon-button discussion-start"
          onClick={() => signIn(location.pathname + location.search)}
          aria-label={t('discussions.signInToComment')}
          title={t('discussions.signInToComment')}
        >
          <Icon name="lock" />
        </button>
      ) : (
        <p className="discussion-note">{t('discussions.readOnly')}</p>
      )}
      {thread.comments.length > 1 && !focused ? (
        <div className="discussion-sort" role="group" aria-label={t('discussions.sort.label')}>
          {(['oldest', 'most-referenced'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={sort === value}
              className={sort === value ? 'active' : undefined}
              onClick={() => setSort(value)}
            >
              {t(`discussions.sort.${value === 'oldest' ? 'oldest' : 'mostReferenced'}`)}
            </button>
          ))}
        </div>
      ) : null}
      {focused ? (
        <button
          type="button"
          className="icon-button"
          onClick={() => setFocusId(null)}
          aria-label={t('discussions.backToAll')}
          title={t('discussions.backToAll')}
        >
          <Icon name="arrowBack" />
        </button>
      ) : null}
      {shown.length === 0 ? (
        <p className="discussion-note">{t('discussions.empty')}</p>
      ) : (
        <ol className="discussion-comments">
          {shown.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              depth={1}
              maxDepth={phone ? DEPTH_PHONE : DEPTH_DESKTOP}
              kind={kind}
              artifactId={artifactId}
              canComment={thread.canComment}
              signedIn={signedIn}
              moderator={thread.moderator}
              describeAnchor={describeAnchor}
              onShowAnchor={onShowAnchor}
              onChanged={reload}
              onContinue={setFocusId}
            />
          ))}
        </ol>
      )}
    </section>
  );
}
